import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import {
  Copy,
  Loader2,
  BarChart3,
  Trophy,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Download,
  FileText,
  Calendar,
  Sparkles,
  Users,
  Twitter,
  Instagram,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const WeeklyContentTab = ({ token, recap, quickStats, contentLoading, fetchWeeklyContent, refreshWeeklyContent }) => {
  const [contentSubTab, setContentSubTab] = useState('overview');

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard!`);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const exportAsJSON = () => {
    if (!recap) return;
    const blob = new Blob([JSON.stringify(recap, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `trader-titans-recap-week-${recap.meta.week}.json`;
    a.click();
    toast.success("JSON exported!");
  };

  const exportAsMarkdown = async () => {
    try {
      const res = await fetch(`${API_URL}/api/content/export?format=markdown`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const blob = new Blob([data.content], { type: "text/markdown" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `trader-titans-recap-week-${recap?.meta?.week || "current"}.md`;
        a.click();
        toast.success("Markdown exported!");
      }
    } catch {
      toast.error("Export failed");
    }
  };

  if (contentLoading && !recap) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!recap) {
    return (
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="py-12 text-center">
          <FileText className="h-16 w-16 mx-auto text-slate-600 mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Content Unavailable</h3>
          <p className="text-slate-400 mb-6">Unable to load weekly content data.</p>
          <Button onClick={fetchWeeklyContent}>
            <RefreshCw className="h-4 w-4 mr-2" /> Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      {/* Header with export buttons */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <Card className="bg-gradient-to-r from-emerald-900/50 to-blue-900/50 border-emerald-500/30 flex-1">
          <CardContent className="py-4">
            <div className="flex items-center gap-4">
              <Calendar className="w-10 h-10 text-emerald-400" />
              <div>
                <h2 className="text-xl font-bold text-white">
                  Week {recap.meta.week} Recap
                </h2>
                <p className="text-slate-300">
                  {recap.meta.week_dates.start} - {recap.meta.week_dates.end}
                </p>
              </div>
              <Badge className="bg-emerald-500/20 text-emerald-400 ml-auto">
                {recap.meta.season}
              </Badge>
            </div>
          </CardContent>
        </Card>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportAsJSON} data-testid="export-json-btn">
            <Download className="w-4 h-4 mr-2" /> JSON
          </Button>
          <Button variant="outline" onClick={exportAsMarkdown} data-testid="export-md-btn">
            <FileText className="w-4 h-4 mr-2" /> Markdown
          </Button>
          <Button onClick={refreshWeeklyContent} disabled={contentLoading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${contentLoading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Content Sub-tabs */}
      <Tabs value={contentSubTab} onValueChange={setContentSubTab}>
        <TabsList className="bg-slate-800/50 border border-slate-700">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="script">Video Script</TabsTrigger>
          <TabsTrigger value="social">Social Posts</TabsTrigger>
          <TabsTrigger value="data">Raw Data</TabsTrigger>
        </TabsList>

        {/* Overview Sub-tab */}
        <TabsContent value="overview" className="space-y-6 mt-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-slate-900/50 border-slate-700">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Total Players</p>
                    <p className="text-2xl font-bold text-white">{recap.platform_stats.total_players.toLocaleString()}</p>
                  </div>
                  <Users className="w-8 h-8 text-blue-400" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/50 border-slate-700">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">New This Week</p>
                    <p className="text-2xl font-bold text-emerald-400">+{recap.platform_stats.new_players_this_week}</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-emerald-400" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/50 border-slate-700">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Pro Users</p>
                    <p className="text-2xl font-bold text-amber-400">{recap.platform_stats.pro_players}</p>
                  </div>
                  <Sparkles className="w-8 h-8 text-amber-400" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/50 border-slate-700">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Participants</p>
                    <p className="text-2xl font-bold text-purple-400">{recap.leaderboard_highlights.total_participants}</p>
                  </div>
                  <BarChart3 className="w-8 h-8 text-purple-400" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Champion & Top 5 */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="bg-gradient-to-br from-amber-900/30 to-slate-900 border-amber-500/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-400">
                  <Trophy className="w-5 h-5" /> Champion of the Week
                </CardTitle>
              </CardHeader>
              <CardContent>
                {recap.leaderboard_highlights.champion_of_the_week ? (
                  <div className="text-center py-4">
                    <p className="text-3xl font-bold text-white mb-2">{recap.leaderboard_highlights.champion_of_the_week.username}</p>
                    <p className="text-5xl font-bold text-amber-400">{recap.leaderboard_highlights.champion_of_the_week.total_points}</p>
                    <p className="text-slate-400 mt-1">points this week</p>
                  </div>
                ) : (
                  <p className="text-slate-400 text-center py-4">No champion data yet</p>
                )}
              </CardContent>
            </Card>

            <Card className="bg-slate-900/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Weekly Top 5</CardTitle>
                <CardDescription>Best performers this week</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {recap.leaderboard_highlights.weekly_top_5.map((player, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-slate-800/50">
                      <div className="flex items-center gap-3">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${
                          idx === 0 ? "bg-amber-500 text-black" :
                          idx === 1 ? "bg-slate-400 text-black" :
                          idx === 2 ? "bg-amber-700 text-white" :
                          "bg-slate-600 text-white"
                        }`}>{idx + 1}</span>
                        <span className="text-white font-medium">{player.username}</span>
                      </div>
                      <span className="text-emerald-400 font-bold">{player.total_points} pts</span>
                    </div>
                  ))}
                  {recap.leaderboard_highlights.weekly_top_5.length === 0 && (
                    <p className="text-slate-400 text-center py-4">No scores yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Market Movers */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="bg-slate-900/50 border-slate-700">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-emerald-400">
                  <TrendingUp className="w-5 h-5" /> Top Gainers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {recap.market_movers.top_gainers.map((mover, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-emerald-950/30 border border-emerald-900/30">
                      <div>
                        <p className="text-white font-medium">{mover.symbol}</p>
                        <p className="text-slate-400 text-sm">{mover.name}</p>
                      </div>
                      <span className="text-emerald-400 font-bold">+{mover.change_pct}%</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-900/50 border-slate-700">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-400">
                  <TrendingDown className="w-5 h-5" /> Top Losers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {recap.market_movers.top_losers.map((mover, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-red-950/30 border border-red-900/30">
                      <div>
                        <p className="text-white font-medium">{mover.symbol}</p>
                        <p className="text-slate-400 text-sm">{mover.name}</p>
                      </div>
                      <span className="text-red-400 font-bold">{mover.change_pct}%</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Fun Facts */}
          {recap.fun_facts.length > 0 && (
            <Card className="bg-slate-900/50 border-slate-700">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Sparkles className="w-5 h-5 text-purple-400" /> Fun Facts
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-3">
                  {recap.fun_facts.map((fact, idx) => (
                    <div key={idx} className="p-3 rounded-lg bg-purple-950/20 border border-purple-900/30">
                      <p className="text-slate-300">{fact}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Video Script Sub-tab */}
        <TabsContent value="script" className="mt-6">
          <Card className="bg-slate-900/50 border-slate-700">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-white">Video Script Template</CardTitle>
                <CardDescription>Ready-to-use script for your weekly recap video</CardDescription>
              </div>
              <Button onClick={() => copyToClipboard(recap.script_template, "Script")} data-testid="copy-script-btn">
                <Copy className="w-4 h-4 mr-2" /> Copy Script
              </Button>
            </CardHeader>
            <CardContent>
              <div className="bg-slate-950 rounded-lg p-4 border border-slate-700 max-h-[600px] overflow-y-auto">
                <pre className="text-slate-300 whitespace-pre-wrap font-mono text-sm">{recap.script_template}</pre>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Social Posts Sub-tab */}
        <TabsContent value="social" className="space-y-6 mt-6">
          {quickStats && (
            <>
              <Card className="bg-slate-900/50 border-slate-700">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Twitter className="w-5 h-5 text-blue-400" />
                    <CardTitle className="text-white">Twitter/X Post</CardTitle>
                  </div>
                  <Button variant="outline" onClick={() => copyToClipboard(quickStats.social_snippets.twitter, "Twitter post")} data-testid="copy-twitter-btn">
                    <Copy className="w-4 h-4 mr-2" /> Copy
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="bg-slate-950 rounded-lg p-4 border border-slate-700">
                    <pre className="text-slate-300 whitespace-pre-wrap text-sm">{quickStats.social_snippets.twitter}</pre>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900/50 border-slate-700">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Instagram className="w-5 h-5 text-pink-400" />
                    <CardTitle className="text-white">Instagram Caption</CardTitle>
                  </div>
                  <Button variant="outline" onClick={() => copyToClipboard(quickStats.social_snippets.instagram, "Instagram caption")} data-testid="copy-instagram-btn">
                    <Copy className="w-4 h-4 mr-2" /> Copy
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="bg-slate-950 rounded-lg p-4 border border-slate-700">
                    <pre className="text-slate-300 whitespace-pre-wrap text-sm">{quickStats.social_snippets.instagram}</pre>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Raw Data Sub-tab */}
        <TabsContent value="data" className="mt-6">
          <Card className="bg-slate-900/50 border-slate-700">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-white">Raw JSON Data</CardTitle>
                <CardDescription>Complete weekly recap data for AI/LLM processing</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => copyToClipboard(JSON.stringify(recap, null, 2), "JSON data")} data-testid="copy-json-btn">
                  <Copy className="w-4 h-4 mr-2" /> Copy JSON
                </Button>
                <Button onClick={exportAsJSON}>
                  <Download className="w-4 h-4 mr-2" /> Download
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="bg-slate-950 rounded-lg p-4 border border-slate-700 max-h-[500px] overflow-y-auto">
                <pre className="text-slate-300 whitespace-pre-wrap font-mono text-xs">{JSON.stringify(recap, null, 2)}</pre>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </>
  );
};
